import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Catalogo "Sfoglia Listino" full-screen: ricerca + categoria, seleziona un articolo per la riga target. */
export default function CatalogoDialog({ open, onOpenChange, targetIndex, onSelect }) {
  const [articoli, setArticoli] = useState([]);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setCategoria('');
    if (articoli.length === 0) {
      axios.get(`${API}/listino`)
        .then((res) => setArticoli(res.data || []))
        .catch(() => toast.error('Errore caricamento listino'));
    }
  }, [open, articoli.length]);

  const categorie = [...new Set(articoli.map(a => a.categoria).filter(Boolean))].sort();

  const filtrati = articoli.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      a.codice?.toLowerCase().includes(q) ||
      a.descrizione?.toLowerCase().includes(q) ||
      a.categoria?.toLowerCase().includes(q) ||
      a.note?.toLowerCase().includes(q);
    const matchCat = !categoria || a.categoria === categoria;
    return matchSearch && matchCat;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl sm:h-[90vh] rounded-md p-0 flex flex-col" data-testid="catalogo-dialog">
        <DialogHeader className="p-4 border-b border-border">
          <DialogTitle className="font-mono uppercase tracking-wider flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Catalogo Articoli — Riga {targetIndex + 1}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 border-b border-border bg-muted/30 space-y-2">
          <div className="grid grid-cols-12 gap-2">
            <Input
              autoFocus
              placeholder="Cerca per codice, descrizione, categoria o note... (anche parziale)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="col-span-12 md:col-span-9 rounded-md"
              data-testid="catalogo-search"
            />
            <Select value={categoria || 'all'} onValueChange={(v) => setCategoria(v === 'all' ? '' : v)}>
              <SelectTrigger className="col-span-12 md:col-span-3 rounded-md" data-testid="catalogo-categoria">
                <SelectValue placeholder="Tutte le categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categorie.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {filtrati.length} articoli trovati su {articoli.length} totali
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {filtrati.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground font-mono text-sm">
              Nessun articolo trovato
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtrati.map((art) => (
                <button
                  key={art.id}
                  type="button"
                  onClick={() => onSelect(art)}
                  className="text-left rounded-md border border-border bg-card hover:border-primary hover:shadow-md transition p-3 group"
                  data-testid={`catalogo-item-${art.codice}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-mono text-xs font-bold text-primary">{art.codice}</span>
                    <span className="font-bold text-emerald-600 text-sm">€ {Number(art.prezzo || 0).toFixed(2)}</span>
                  </div>
                  <p className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-primary">
                    {art.descrizione}
                  </p>
                  <div className="flex justify-between items-center mt-2 text-[11px] text-muted-foreground">
                    <span>{art.categoria || '—'}</span>
                    <span className="font-mono">{art.unita_misura || 'pz'}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
